import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  Grid, MenuItem, TextField, Typography
} from "@material-ui/core";
import api from "../../services/api";

// V4 beta: extensão Chrome coleta fichas públicas a pedido do usuário.
// Sem envio automático, migrations ou gravação no CRM. Rever termos de dados.
const BLUE = "#0057FF", NAVY = "#010B36", BORDER = "#E1E9F4";
const CARD = { border: `1px solid ${BORDER}`, borderRadius: 15, boxShadow: "none" };
const MUTED = { fontSize: 12, color: "#64748B" };
const UF = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const CNAES = [["9602502","Estética"],["8630504","Odontologia"],["8630503","Consultórios médicos"],["6622300","Corretoras de seguros"],["6920601","Contabilidade"],["6911701","Advocacia"],["6821801","Imobiliárias"],["9313100","Academias"],["5611201","Restaurantes"],["4520001","Oficinas mecânicas"],["8121400","Serviços de limpeza"]];
const STATUSES = [
  { id:"new", label:"Não prospectado", color:"#0057FF" },
  { id:"attempted", label:"Tentei / sem contato", color:"#F59E0B" },
  { id:"contacted", label:"Contato realizado", color:"#0EA5E9" },
  { id:"opportunity", label:"Oportunidade", color:"#13955F" },
  { id:"blocked", label:"Não contatar", color:"#DC2626" }
];
const norm = x => String(x == null ? "" : x).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
const digits = x => String(x == null ? "" : x).replace(/\D/g, "");
const hasEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const safeUrl = raw => {
  const s = String(raw || "").trim();
  if (!s || /[\s<>"']/g.test(s)) return "";
  try { const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`); return ["http:", "https:"].includes(u.protocol) ? u.href : ""; }
  catch { return ""; }
};
const VALID_DDD = new Set([11,12,13,14,15,16,17,18,19,21,22,24,27,28,31,32,33,34,35,37,38,41,42,43,44,45,46,47,48,49,51,53,54,55,61,62,63,64,65,66,67,68,69,71,73,74,75,77,79,81,82,83,84,85,86,87,88,89,91,92,93,94,95,96,97,98,99]);
// Telefone brasileiro: aceita codigo 55 e o prefixo de discagem 0 somente quando valido.
// NUNCA completar automaticamente o nono digito de um celular antigo ou truncado.
const phoneBR = raw => {
  let p = digits(raw);
  if ((p.length === 12 || p.length === 13) && p.startsWith("55")) p = p.slice(2);
  if ((p.length === 11 || p.length === 12) && p.startsWith("0") && VALID_DDD.has(Number(p.slice(1,3)))) p = p.slice(1);
  if (p.length !== 10 && p.length !== 11) return "";
  if (!VALID_DDD.has(Number(p.slice(0,2)))) return "";
  const local = p.slice(2);
  if (p.length === 10 && !/^[2-5]\d{7}$/.test(local)) return "";
  if (p.length === 11 && !/^9\d{8}$/.test(local)) return "";
  if (/^(\d)\1+$/.test(local)) return "";
  return p;
};
const fmtPhone = raw => {
  const p = phoneBR(raw);
  return p.length === 11 ? `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}` : p.length === 10 ? `(${p.slice(0,2)}) ${p.slice(2,6)}-${p.slice(6)}` : "Não informado";
};
const finiteCoord = (v, max) => {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) && Math.abs(n) <= max ? n : null;
};
const get = (row, ...names) => {
  for (const name of names) {
    if (row[name] != null && row[name] !== "") return row[name];
    const hit = Object.keys(row).find(key => norm(key) === norm(name));
    if (hit && row[hit] != null && row[hit] !== "") return row[hit];
  }
  return "";
};
const keyFor = row => {
  if(row.contactId) return `base:${row.contactId}`;
  const map = String(row.mapsUrl || "").split("?")[0].replace(/\/$/,"");
  if (map) return `maps:${map}`;
  const cnpj = digits(row.cnpj);
  if (cnpj.length === 14) return `cnpj:${cnpj}`;
  const phone = phoneBR(row.phone);
  if (phone && norm(row.name)) return `phone:${phone}|${norm(row.name)}`;
  return `name:${norm(row.name)}|${norm(row.address || row.city)}`;
};
const normalizeLead = raw => {
  const decision = raw.decisionMaker && typeof raw.decisionMaker === "object" ? raw.decisionMaker : {};
  const position = raw.position && typeof raw.position === "object" ? raw.position : {};
  const item = {
    contactId: get(raw,"source")==="Korax - minha base" ? String(get(raw,"contactId","id")||"") : "",
    priority: Number(get(raw,"priority")) || 0,
    suggestion: String(get(raw,"suggestion")||""),
    name: String(get(raw,"name","nome","nome fantasia","empresa","razao_social","razao social","legalName") || "").trim(),
    // Alguns cards do Maps trazem a nota como categoria (ex: 4,0(46)).
    category: (() => { const c=String(get(raw,"category","categoria","segmento","cnaeDescription") || "").trim(); return /^[1-5][.,]\d\s*\(\s*[\d.,]+\s*\)$/.test(c) ? "" : c; })(),
    address: String(get(raw,"address","endereco","endereço","logradouro") || "").trim(),
    city: String(get(raw,"city","cidade","municipio","município") || "").trim(),
    uf: String(get(raw,"uf","state","estado") || "").trim(),
    phone: String(get(raw,"phone","telefone","celular","whatsapp","companyPhone","number") || "").trim(),
    email: String(get(raw,"email","e-mail","e mail","companyEmail","email cadastral") || "").trim(),
    website: String(get(raw,"website","site","url site","dominio") || "").trim(),
    mapsUrl: safeUrl(get(raw,"mapsUrl","linkmaps","link maps","google maps url","maps url")),
    cnpj: digits(get(raw,"cnpj","documento")),
    rating: Number(String(get(raw,"rating","nota","avaliacao","avaliação") || "").replace(",",".")) || 0,
    reviews: Number(get(raw,"reviews","avaliacoes","avaliações","reviews_count","quantidade de avaliações")) || 0,
    lat: finiteCoord(get(raw,"lat","latitude") || position.lat,90),
    lng: finiteCoord(get(raw,"lng","lon","long","longitude") || position.lng,180),
    decisionMaker: String(decision.name || get(raw,"probableDecisionMaker","decisor","responsavel","responsável") || "").trim(),
    source: String(get(raw,"source","origem") || "Lista importada (origem declarada)").trim(),
    status: String(get(raw,"status","prospectingStatus") || "new"),
    note: String(get(raw,"note","notes","observacoes","observações") || ""),
    lastContactAt: String(get(raw,"lastContactAt") || ""),
    cnpjSource: String(raw.cnpjSource || ""),
    emailSource: String(raw.emailSource || ""),
    emailStatus: String(raw.emailStatus || ""),
    emailCheckedAt: String(raw.emailCheckedAt || ""),
    emailAttemptAt: String(raw.emailAttemptAt || ""),
    phoneSource: String(raw.phoneSource || ""),
    cnpjLookupAt: String(raw.cnpjLookupAt || "")
  };
  if (!STATUSES.some(s=>s.id===item.status)) item.status = "new";
  if (item.lat === 0 && item.lng === 0) { item.lat=null; item.lng=null; }
  return { ...item, id:keyFor(item) };
};
const dedupe = items => {
  const map = new Map();
  items.forEach(raw => {
    const x = normalizeLead(raw);
    if (!x.name) return;
    const previous = map.get(x.id);
    if (!previous) map.set(x.id,x);
    else {
      const combined = { ...previous };
      Object.keys(x).forEach(k=>{
        // Se a ficha nova traz um telefone de formato valido e o antigo nao, usar o novo.
        // Nunca substituir automaticamente dois numeros validos diferentes.
        if(k==='phone' && !phoneBR(combined.phone) && phoneBR(x.phone)) { combined.phone=x.phone; return; }
        if ((combined[k] === '' || combined[k] == null || combined[k] === 0) && x[k] !== '' && x[k] != null) combined[k] = x[k];
      });
      map.set(x.id,combined);
    }
  });
  return Array.from(map.values());
};
const parseCSV = raw => {
  const text = String(raw).replace(/^\uFEFF/,"");
  const head = text.split(/\r?\n/)[0] || "";
  const sep = (head.match(/;/g)||[]).length >= (head.match(/,/g)||[]).length ? ";" : ",";
  const rows = []; let col="", row=[], inQuote=false;
  for(let i=0;i<text.length;i+=1){
    const c=text[i];
    if(c==='"'){if(inQuote && text[i+1]==='"'){col+='"'; i+=1;} else inQuote=!inQuote;}
    else if(c===sep && !inQuote){row.push(col);col="";}
    else if((c==="\r"||c==="\n")&&!inQuote){if(c==="\r"&&text[i+1]==="\n")i+=1;row.push(col);col="";if(row.some(v=>v.trim()))rows.push(row);row=[];}
    else col+=c;
  }
  if(inQuote) throw new Error("O arquivo CSV possui aspas sem fechamento.");
  row.push(col);if(row.some(v=>v.trim()))rows.push(row);
  const headers=rows.shift();
  if(!headers || !rows.length)throw new Error("O CSV não possui linhas suficientes.");
  return rows.map(cells=>Object.fromEntries(headers.map((h,i)=>[h.trim(),cells[i]||""])));
};
const exportJSON = (items, title="korax-prospeccao") => {
  const blob = new Blob([JSON.stringify({ version:"3.0", exportedAt:new Date().toISOString(), leads:items },null,2)],{type:"application/json"});
  const url=URL.createObjectURL(blob),a=document.createElement("a");
  a.href=url;a.download=`${title}-${new Date().toISOString().slice(0,10)}.json`;document.body.appendChild(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(url),2000);
};
const urlMaps = row => row.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([row.name,row.address,row.city,row.uf].filter(Boolean).join(" "))}`;
const searchMaps = (term,city,uf) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([term,city,uf].filter(Boolean).join(" "))}`;
const haversine = (a,b) => {
  const R=6371, rad=Math.PI/180;
  const dLat=(b.lat-a.lat)*rad,dLng=(b.lng-a.lng)*rad;
  const h=Math.sin(dLat/2)**2+Math.cos(a.lat*rad)*Math.cos(b.lat*rad)*Math.sin(dLng/2)**2;
  return 2*R*Math.atan2(Math.sqrt(h),Math.sqrt(1-h));
};
const datePt = value => {if(!value)return "Nunca";const d=new Date(value);return Number.isFinite(d.getTime())?d.toLocaleDateString("pt-BR"):"Não informado";};

// V4.3 — Correspondencia conservadora entre mapa e CNPJ. Nao atribuir e-mail por semelhanca fraca.
const inferCnae = segment => {
  const s=norm(segment);
  if (/odont|dentist|dental/.test(s)) return "8630504";
  if (/segur|corretora/.test(s)) return "6622300";
  if (/medic|consultorio/.test(s)) return "8630503";
  if (/contab/.test(s)) return "6920601";
  if (/advog|juridic/.test(s)) return "6911701";
  if (/imobili|corretor de imoveis/.test(s)) return "6821801";
  if (/academ|fitness|muscul/.test(s)) return "9313100";
  if (/restaurante|comida|lanchonet/.test(s)) return "5611201";
  if (/estetic|beleza/.test(s)) return "9602502";
  return "";
};
const normalizeCompanyName = s => norm(s).replace(/\b(?:ltda|mei|me|eireli|sa|s a|sociedade limitada|unipessoal)\b/g," ").replace(/\s+/g," ").trim();
const nameTokens = s => [...new Set(normalizeCompanyName(s).split(" ").filter(x=>x.length>=2))];
const similarity = (a,b) => {
  const x=nameTokens(a),y=nameTokens(b);
  if (!x.length||!y.length) return 0;
  const A=new Set(x),B=new Set(y);
  const intersection=x.filter(k=>B.has(k)).length;
  return intersection / (A.size+B.size-intersection);
};
const candidateKey = row => String(digits(row.cnpj));
const mapsKey = row => String(row.mapsUrl||"").split("?")[0].replace(/\/$/,"");
const sameCnpjMatch = (lead,candidate) => {
  const known=digits(lead.cnpj);
  return known.length===14 && known===digits(candidate.cnpj);
};
const checkReceiptMatch = (lead,candidate) => {
  if(sameCnpjMatch(lead,candidate))return {rank:3,reason:"CNPJ identico"};
  const leadName=normalizeCompanyName(lead.name);
  const companyName=normalizeCompanyName(candidate.name);
  if (!leadName||!companyName||leadName.length<8) return null;
  const exact=leadName===companyName;
  const bairro=norm(candidate.neighborhood);
  const address=norm(lead.address);
  const bairroAgrees=!!bairro && bairro.length>=5 && !!address && address.includes(bairro);
  const score=similarity(leadName,companyName);
  // Fantasia iguais + mesmo municipio informado nos filtros; senão exigir bairro e >=3 tokens.
  if(exact && nameTokens(leadName).length>=2) return {rank:2,reason:"Nome fantasia coincidente (conferir CNPJ)"};
  if(bairroAgrees && nameTokens(leadName).length>=3 && score>=0.92) return {rank:1,reason:"Nome e bairro coincidentes (conferir CNPJ)"};
  return null;
};
const matchReceiptCandidates = (leads,candidates) => {
  const all=[];
  leads.filter(l=>l.mapsUrl).forEach(lead=>{
    const ranked=candidates.map(candidate=>({candidate,result:checkReceiptMatch(lead,candidate)}))
      .filter(x=>x.result).sort((a,b)=>b.result.rank-a.result.rank);
    if(!ranked.length)return;
    if(ranked[1] && ranked[0].result.rank===ranked[1].result.rank)return; // Homonimos: nao anexar.
    all.push({lead,candidate:ranked[0].candidate,reason:ranked[0].result.reason,rank:ranked[0].result.rank});
  });
  const counts=new Map();
  all.forEach(x=>counts.set(candidateKey(x.candidate),(counts.get(candidateKey(x.candidate))||0)+1));
  return all.filter(x=>counts.get(candidateKey(x.candidate))===1); // Mesmo CNPJ nao pode qualificar filiais diferentes.
};

const opportunity = item => {
  if(item.contactId && item.priority)return {score:item.priority,reasons:[item.suggestion||"Relacionamento existente"]};
  let score=12, reasons=[];
  if(!safeUrl(item.website)){score+=28;reasons.push("Sem site informado nesta fonte");}
  if(item.reviews && item.reviews<50){score+=16;reasons.push("Menos de 50 avaliações");}
  if(item.rating && item.rating<4.2){score+=13;reasons.push("Reputação pode melhorar");}
  if(phoneBR(item.phone)){score+=12;reasons.push("Telefone disponível");}
  if(hasEmail(item.email)){score+=15;reasons.push("E-mail disponível");}
  if(item.decisionMaker){score+=6;reasons.push("Responsável identificado");}
  return { score:Math.max(0,Math.min(score,100)), reasons:reasons.slice(0,3) };
};
const draftFor = (lead, offer, channel) => {
  const name=lead.name || "sua empresa";
  const observation= !safeUrl(lead.website) ? "não encontrei um site próprio informado" : lead.reviews && lead.reviews<50 ? "vi uma oportunidade de fortalecer a presença digital da empresa" : "identifiquei uma oportunidade de organizar melhor o atendimento comercial";
  const intro=`Encontrei a ${name} ao pesquisar negócios da região e ${observation}.`;
  if(channel==="call")return {channel,lead,subject:"",body:`ABERTURA\nOlá, tudo bem? Meu nome é [seu nome]. Poderia falar com a pessoa responsável pelo atendimento ou pelo comercial da ${name}?\n\nCONTEXTO\n${intro}\n\nQUALIFICAÇÃO\n1. Como vocês recebem novos contatos hoje?\n2. Quem acompanha as mensagens e os pedidos de orçamento?\n3. Existe algum processo de retorno para quem não responde na primeira tentativa?\n\nPROPOSTA\nTrabalho com ${offer}.\n\nENCERRAMENTO\nSe fizer sentido, podemos agendar uma conversa rápida para avaliar a operação? Se não houver interesse, registro para não insistir.`};
  if(channel==="whatsapp")return {channel,lead,subject:"",body:`Olá, tudo bem? Sou [seu nome]. ${intro}\n\nTrabalho com ${offer}. Quem seria a melhor pessoa para uma conversa rápida sobre isso? Se não houver interesse, pode me avisar.`};
  return {channel,lead,subject:`Uma ideia para ${name}`,body:`Olá, tudo bem?\n\n${intro}\n\nTrabalho com ${offer}. Gostaria de entender se faz sentido uma conversa rápida com a pessoa responsável.\n\nSe não for relevante, é só me avisar que não voltarei a entrar em contato.\n\nObrigado!`};
};

let leafletPromise;
const loadLeaflet=()=>{
  if(typeof window==="undefined")return Promise.reject(new Error("Mapa indisponível"));
  if(window.L)return Promise.resolve(window.L);
  if(leafletPromise)return leafletPromise;
  leafletPromise=new Promise((resolve,reject)=>{
    const link=document.createElement("link"); link.rel="stylesheet"; link.href="https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css";document.head.appendChild(link);
    const s=document.createElement("script");s.src="https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js";s.async=true;
    s.onload=()=>window.L?resolve(window.L):reject(new Error("Biblioteca do mapa indisponível"));
    s.onerror=()=>reject(new Error("Não foi possível carregar o mapa. Verifique conexão ou bloqueador de scripts."));
    document.head.appendChild(s);
  }).catch(err=>{leafletPromise=null;throw err;});
  return leafletPromise;
};
const MapPanel=({leads,selected,onSelect})=>{
  const element=useRef(null), map=useRef(null), group=useRef(null);
  const [mapError,setMapError]=useState("");
  const [mapReady,setMapReady]=useState(0);
  useEffect(()=>{
    let active=true;
    loadLeaflet().then(L=>{
      if(!active || !element.current || map.current)return;
      map.current=L.map(element.current,{zoomControl:true}).setView([-23.454,-46.533],12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{maxZoom:19,attribution:'&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>'}).addTo(map.current);
      group.current=L.layerGroup().addTo(map.current);
      setMapReady(ready=>ready+1);
      setTimeout(()=>{if(active && map.current)map.current.invalidateSize();},130);
    }).catch(err=>{if(active)setMapError(err.message);});
    return()=>{active=false; if(map.current){map.current.remove();map.current=null;group.current=null;}};
  },[]);
  useEffect(()=>{
    if(!map.current||!group.current||!window.L)return;
    const L=window.L;group.current.clearLayers();const bounds=[];
    leads.filter(l=>l.lat!=null&&l.lng!=null).forEach((lead,i)=>{
      const chosen=lead.id===selected;
      const icon=L.divIcon({className:"",html:`<div style="width:${chosen?35:28}px;height:${chosen?35:28}px;border-radius:50%;background:${chosen?'#010B36':'#15985D'};color:#fff;display:flex;align-items:center;justify-content:center;font:700 12px Arial;border:3px solid white;box-shadow:0 3px 8px #11285c55">${i+1}</div>`,iconSize:[35,35],iconAnchor:[16,16]});
      const marker=L.marker([lead.lat,lead.lng],{icon});
      const tooltip=document.createElement("span");tooltip.textContent=lead.name;
      marker.bindTooltip(tooltip);marker.on("click",()=>onSelect(lead.id));group.current.addLayer(marker);bounds.push([lead.lat,lead.lng]);
    });
    if(bounds.length===1)map.current.setView(bounds[0],14);
    else if(bounds.length>1)map.current.fitBounds(bounds,{padding:[35,35],maxZoom:15});
    setTimeout(()=>map.current&&map.current.invalidateSize(),80);
  },[leads,selected,onSelect,mapError,mapReady]);
  const located=leads.filter(l=>l.lat!=null && l.lng!=null).length;
  return <Box style={{position:"relative",minHeight:520,borderRadius:15,overflow:"hidden",background:"#EAF0F2"}}>
    <div ref={element} style={{height:560,width:"100%",zIndex:0}} />
    <Box style={{position:"absolute",top:14,left:14,zIndex:401,background:"#fff",padding:"9px 12px",borderRadius:10,maxWidth:"78%",boxShadow:"0 2px 12px #00000018"}}>
      <Typography style={{fontWeight:800,color:NAVY,fontSize:14}}>Mapa comercial da região</Typography>
      <Typography style={MUTED}>{located} de {leads.length} empresas com coordenadas</Typography>
    </Box>
    {mapError && <Box style={{position:"absolute",top:100,left:20,right:20,zIndex:402,background:"white",padding:14,borderRadius:10}}><Typography color="error">{mapError}</Typography><Button href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer">Abrir OpenStreetMap</Button></Box>}
    {!mapError && !located && <Box style={{position:"absolute",bottom:18,left:18,right:18,zIndex:400,background:"#fffffff2",padding:15,borderRadius:12}}>
      <Typography style={{fontWeight:700,color:NAVY}}>Aguardando negócios com localização</Typography>
      <Typography style={MUTED}>Pesquise usando a extensão Korax Collector ou importe um JSON/CSV com latitude e longitude para visualizar os marcadores.</Typography>
    </Box>}
  </Box>;
};
const Metric=({value,label,color=NAVY})=><Box style={{flex:"1 1 135px",minWidth:0,background:"#fff",border:`1px solid ${BORDER}`,padding:"11px 14px",borderRadius:12,boxShadow:"0 2px 10px rgba(9,28,65,.025)"}}>
  <Typography style={{fontSize:24,fontWeight:850,color,lineHeight:1.2}}>{value}</Typography>
  <Typography style={{fontSize:11,color:"#718099",fontWeight:650,marginTop:3}}>{label}</Typography>
</Box>;

// V4.5: ficha comercial compacta. Mantem os dados e fluxos existentes; mostra detalhes sob demanda.
const ProspectCard = ({item, index, active, expanded, onSelect, onToggle, status, onStatus, onDraft, onNotes, onSync, onCnpj, syncDisabled, note, lastContactAt}) => {
  const phone = phoneBR(item.phone);
  const email = hasEmail(item.email);
  const website = safeUrl(item.website);
  const blocked = status === "blocked";
  const potential = opportunity(item);
  const statusMeta = STATUSES.find(s => s.id === status) || STATUSES[0];
  const hasContact = !!phone || email;
  const location = item.address || [item.city, item.uf].filter(Boolean).join(" / ");
  const actionStyle = {textTransform:"none",fontWeight:750,borderRadius:9,fontSize:12,minHeight:30,minWidth:0,padding:"4px 10px",boxShadow:"none"};
  const stop = e => e.stopPropagation();
  const openNotes = e => {stop(e);onNotes(item);};
  const toggle = e => {stop(e);onToggle(item.id);};
  const detailRow = (label, value) => value ? <Box key={label} style={{minWidth:0,marginBottom:9}}>
    <Typography style={{fontSize:10,color:"#8290A7",fontWeight:750,marginBottom:2}}>{label}</Typography>
    <Typography style={{fontSize:12,color:NAVY,overflowWrap:"anywhere"}}>{value}</Typography>
  </Box> : null;

  return <Card onClick={() => onSelect(item.id)} style={{...CARD,marginBottom:9,cursor:"pointer",background:active?"#F9FBFF":"white",borderColor:active?"#8BAEFF":BORDER,boxShadow:active?"0 1px 9px rgba(0,87,255,.07)":"none",transition:"border-color .16s ease, background .16s ease"}}>
    <CardContent style={{padding:"12px 13px 11px"}}>
      {/* Identificacao imediata: nome + avaliacao, sem duplicar insights. */}
      <Box display="flex" alignItems="flex-start" style={{gap:9}}>
        <Box style={{flex:"0 0 25px",height:25,borderRadius:8,background:"#EEF3FF",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:BLUE,fontWeight:850}}>{index+1}</Box>
        <Box style={{flex:1,minWidth:0}}>
          <Typography style={{fontSize:14,fontWeight:850,color:NAVY,lineHeight:1.3,overflowWrap:"break-word"}}>{item.name}</Typography>
          {(item.category||location)&&<Typography style={{fontSize:11,color:"#748298",marginTop:4,lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
            {[item.category,location].filter(Boolean).join(" · ")}
          </Typography>}
        </Box>
        {(item.rating>0||item.reviews>0)&&<Box style={{textAlign:"right",flex:"0 0 auto",minWidth:44}}>
          {item.rating>0&&<Typography style={{fontSize:13,fontWeight:850,color:NAVY,lineHeight:1.25}}><span style={{color:"#E4A83C"}}>★</span> {String(item.rating).replace(".",",")}</Typography>}
          {item.reviews>0&&<Typography style={{fontSize:10,color:"#8490A4"}}>{item.reviews} aval.</Typography>}
        </Box>}
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:7,marginTop:10}}>
        <TextField select size="small" variant="outlined" value={status} onClick={stop} onChange={e=>onStatus(item,e.target.value)}
          style={{minWidth:151,maxWidth:"100%"}} InputProps={{style:{fontSize:11,height:31,borderRadius:9,color:statusMeta.color,fontWeight:750}}}>
          {STATUSES.map(s=><MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
        </TextField>
        <Box title="Potencial indicativo, não equivale a contato validado" style={{background:"#EDF4FF",borderRadius:7,padding:"5px 8px",fontSize:10,fontWeight:800,color:BLUE,whiteSpace:"nowrap"}}>Potencial {potential.score}/100</Box>
        <Box title={hasContact?"Telefone ou e-mail informado, ainda sem verificacao":"Nenhum telefone ou e-mail informado"}
          style={{borderRadius:7,padding:"5px 8px",fontSize:10,fontWeight:800,whiteSpace:"nowrap",background:hasContact?"#EBF8F0":"#F4F6F9",color:hasContact?"#128052":"#748197"}}>
          {hasContact?"Contato informado":"Sem contato"}
        </Box>
      </Box>

      {/* Nao desperdiçar metade do card com quatro campos vazios. */}
      {hasContact&&<Box display="flex" flexWrap="wrap" style={{gap:"5px 12px",marginTop:10,minWidth:0}}>
        {phone&&<Typography style={{fontSize:12,fontWeight:700,color:NAVY}}>☎ {fmtPhone(phone)}</Typography>}
        {email&&<Typography title={item.email} style={{fontSize:12,fontWeight:700,color:NAVY,overflowWrap:"anywhere",minWidth:0}}>✉ {item.email}</Typography>}

      </Box>}

      <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:6,marginTop:10,paddingTop:9,borderTop:"1px solid #EDF1F7"}}>
        {!blocked&&phone&&<Button size="small" variant="contained" onClick={e=>{stop(e);onDraft(item,"whatsapp");}} style={{...actionStyle,background:"#12865B",color:"white"}} title="Numero informado no Maps ou cadastro; disponibilidade no WhatsApp nao verificada">WhatsApp?</Button>}
        {!blocked&&!phone&&email&&<Button size="small" variant="contained" onClick={e=>{stop(e);onDraft(item,"email");}} style={{...actionStyle,background:BLUE,color:"white"}}>Preparar e-mail</Button>}
        {!blocked&&phone&&<Button size="small" onClick={e=>{stop(e);onDraft(item,"call");}} style={{...actionStyle,color:NAVY}}>Ligar</Button>}
        {!blocked&&phone&&email&&<Button size="small" onClick={e=>{stop(e);onDraft(item,"email");}} style={{...actionStyle,color:NAVY}}>E-mail</Button>}
        <Button size="small" variant={!hasContact?"outlined":"text"} href={urlMaps(item)} target="_blank" rel="noopener noreferrer" onClick={stop} style={{...actionStyle,color:BLUE,borderColor:"#C8DAFF"}}>Google Maps</Button>
        {!blocked && item.mapsUrl && !phone && <Button size="small" disabled={syncDisabled} title="Abra primeiro esta empresa no Google Maps e depois sincronize telefone, endereco e site da ficha aberta. Nao abre abas extras." onClick={e=>{stop(e);onSync(item);}} style={{...actionStyle,color:BLUE,background:"#F0F6FF"}}>Sincronizar ficha</Button>}
        {!blocked&&<Button size="small" onClick={e=>{stop(e);onCnpj(item);}} style={{...actionStyle,color:BLUE,background:"#EEF3FF"}}>Consultar cadastro</Button>}
        <Button size="small" onClick={toggle} aria-expanded={expanded} style={{...actionStyle,color:"#546780",marginLeft:"auto"}}>{expanded?"Menos detalhes ↑":"Detalhes ↓"}</Button>
      </Box>
      {blocked&&<Typography style={{fontSize:11,color:"#B4232D",marginTop:7}}>Não contatar · contatos desativados</Typography>}

      {expanded&&<Box onClick={stop} style={{marginTop:12,paddingTop:12,borderTop:"1px solid #E8EDF5"}}>
        <Box display="flex" flexWrap="wrap" style={{gap:"2px 18px"}}>
          <Box style={{flex:"1 1 180px",minWidth:0}}>
            {detailRow("ENDEREÇO",item.address || [item.city,item.uf].filter(Boolean).join(" / ") || "Não disponível")}
            {detailRow("CNPJ",item.cnpj ? digits(item.cnpj) : "Não disponível")}
            {detailRow("RESPONSÁVEL",item.decisionMaker || "Não identificado")}
          </Box>
          <Box style={{flex:"1 1 170px",minWidth:0}}>
            {website&&<Box style={{marginBottom:9}}><Typography style={{fontSize:10,color:"#8290A7",fontWeight:750,marginBottom:2}}>SITE</Typography><a href={website} target="_blank" rel="noopener noreferrer" onClick={stop} style={{fontSize:12,color:BLUE,overflowWrap:"anywhere"}}>{new URL(website).hostname}</a></Box>}
            {detailRow("ORIGEM",item.source)}
            {item.emailSource&&detailRow("ORIGEM DO E-MAIL",item.emailSource)}
            {item.cnpjLookupAt&&detailRow("CADASTRO CONSULTADO EM",datePt(item.cnpjLookupAt))}
            {detailRow("ÚLTIMO CONTATO",datePt(lastContactAt))}
          </Box>
        </Box>
        {potential.reasons.length>0&&<Box style={{marginTop:2,background:"#F6F8FC",borderRadius:9,padding:"9px 10px"}}>
          <Typography style={{fontSize:10,color:"#8290A7",fontWeight:800}}>CRITÉRIOS DO POTENCIAL</Typography>
          <Typography style={{fontSize:11,color:"#526178",lineHeight:1.5}}>{potential.reasons.join(" · ")}</Typography>
        </Box>}
        {note&&<Typography style={{fontSize:11,color:"#526178",marginTop:9,whiteSpace:"pre-wrap"}}><b>Nota:</b> {note}</Typography>}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{marginTop:9,gap:6}}>
          <Typography style={{fontSize:10,color:"#8593A7",flex:1}}>Dados de contato informados, não verificados.</Typography>
          <Button size="small" variant="outlined" onClick={openNotes} style={{...actionStyle,color:BLUE,borderColor:"#D1E0FA"}}>{note?"Editar nota":"Adicionar nota"}</Button>
        </Box>
      </Box>}
    </CardContent>
  </Card>;
};

const KoraxProspection=()=>{
  const [mode,setMode]=useState("new"),[tab,setTab]=useState("map");
  const [filters,setFilters]=useState({segment:"clínicas odontológicas",city:"Guarulhos",uf:"SP",cnae:"8630504",limit:25});
  const [extern,setExtern]=useState({items:[],nextCursor:""}),[imported,setImported]=useState([]),[base,setBase]=useState({items:[],total:0,pageSize:30});
  const [basePage,setBasePage]=useState(1),[baseSearch,setBaseSearch]=useState("");
  const [pipeline,setPipeline]=useState({}),[selected,setSelected]=useState("");
  const [expandedCards,setExpandedCards]=useState({});
  const [showTools,setShowTools]=useState(false),[showMoreFilters,setShowMoreFilters]=useState(false),[showSourceInfo,setShowSourceInfo]=useState(false);
  const [term,setTerm]=useState(""),[stage,setStage]=useState("all"),[emailOnly,setEmailOnly]=useState(false),[phoneOnly,setPhoneOnly]=useState(false),[noSite,setNoSite]=useState(false),[highOnly,setHighOnly]=useState(false),[radius,setRadius]=useState("all");
  const [allowImport,setAllowImport]=useState(false),[busy,setBusy]=useState(false),[error,setError]=useState(""),[notice,setNotice]=useState("");
  const [receitaBusy,setReceitaBusy]=useState(false);
  const [receitaProgress,setReceitaProgress]=useState("");
  const [receitaCnae,setReceitaCnae]=useState("");
  const [cnpjView,setCnpjView]=useState(null);
  const [cnpjInput,setCnpjInput]=useState("");
  const [cnpjBusy,setCnpjBusy]=useState(false);
  const [cnpjError,setCnpjError]=useState("");
  const [collectorConnected,setCollectorConnected]=useState(false);
  const [collectorBusy,setCollectorBusy]=useState(false);
  const [collectorVersion,setCollectorVersion]=useState("");
  const collectorAutoRef=useRef({
    active:false,
    steps:0,
    previousPending:null,
    stalls:0
  });
  const [collectorProgress,setCollectorProgress]=useState("");
  const collectorRequest=useRef("");
  const [dialog,setDialog]=useState(null),[offer,setOffer]=useState("uma solução que organiza o atendimento pelo WhatsApp e melhora o acompanhamento de oportunidades comerciais");
  const fileInput=useRef(null),cardRefs=useRef({});
  const setField=(key,value)=>setFilters(old=>({...old,[key]:value}));
  const errorMessage=e=>e?.response?.status===403?"Sua conta não possui permissão para esta consulta.":e?.response?.status===429?"Limite temporário de consultas atingido.":e?.response?.data?.message||e?.message||"Não foi possível concluir a solicitação.";
  const loadCnpj=async(more=false)=>{
    if(digits(filters.cnae).length!==7||!filters.city.trim()) {setError("Informe município e CNAE de sete dígitos.");return;}
    setBusy(true);setError("");setNotice("");
    try{
      const params={uf:filters.uf,city:filters.city,cnae:digits(filters.cnae),limit:filters.limit};
      if(more&&extern.nextCursor)params.cursor=extern.nextCursor;
      const {data}=await api.get("/korax-prospection/external",{params});
      const incoming=dedupe(data.items||[]);
      setExtern(old=>({items:more?dedupe([...old.items,...incoming]):incoming,nextCursor:data.nextCursor||""}));
      setTab("map");setNotice(`${incoming.length} empresas recebidas da base cadastral. Os contatos e endereços não estão verificados.`);
    }catch(e){setError(errorMessage(e));}finally{setBusy(false);}
  };
  const loadBase=async(page=1)=>{
    setBusy(true);setError("");
    try{const {data}=await api.get("/korax-prospection/base",{params:{page,pageSize:30,search:baseSearch}});setBase({...data,items:(data.items||[]).map(x=>({...x,source:"Korax - minha base"}))});setBasePage(page);}
    catch(e){setError(errorMessage(e));}finally{setBusy(false);}
  };
  useEffect(()=>{if(mode==="base")loadBase(1);},[mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    const pageSource="KORAX_PROSPECTION_PAGE_V4", extSource="KORAX_COLLECTOR_EXTENSION_V4";
    const ping=()=>window.postMessage({source:pageSource,type:"PING",requestId:`ping-${Date.now()}`},window.location.origin);
    const handle=event=>{
      if(event.source!==window || event.origin!==window.location.origin)return;
      const msg=event.data;
      if(!msg || msg.source!==extSource)return;
      if(msg.type==="READY" || msg.type==="PONG"){
        setCollectorConnected(true);
        setCollectorVersion(msg.version||msg.response?.version||"");
        return;
      }
      if(msg.type==="PROGRESS"){
        setCollectorProgress(String(msg.progress?.text||"Preparando pesquisa…"));
        return;
      }
      if(msg.type!=="RESULT" || msg.requestId!==collectorRequest.current)return;
      collectorRequest.current="";
      setCollectorBusy(false);
      const result=msg.response||{};
      if(!result.ok){
        collectorAutoRef.current.active=false;
        setError(result.error||"A extensão não conseguiu concluir a pesquisa.");
        setCollectorProgress("");
        return;
      }

      // KORAX V4.11 - Resultado da busca independente de e-mails.
      if(msg.action==="EMAIL"){
        const updates=new Map(
          (Array.isArray(result.emailUpdates)?result.emailUpdates:[])
            .filter(u=>u && u.key && u.website)
            .map(u=>[String(u.key),u])
        );

        setImported(old=>old.map(row=>{
          const key=String(mapsKey(row)).slice(0,256);
          const update=updates.get(key);

          if(!update || hasEmail(row.email))return row;

          const site=safeUrl(row.website);
          if(!site)return row;

          try{
            const original=new URL(site);
            const consulted=new URL(update.website);

            const cleanHost=h=>h.toLowerCase().replace(/^www\./,"");

            if(cleanHost(original.hostname)!==
               cleanHost(consulted.hostname))return row;
          }catch(_){
            return row;
          }

          const discovered=hasEmail(update.email)
            ? String(update.email).trim() : "";

          return {
            ...row,
            email:discovered||row.email,
            emailSource:discovered
              ? String(update.emailSource||update.website)
              : row.emailSource,
            emailStatus:String(update.emailStatus||"not_found"),
            emailCheckedAt:String(
              update.emailCheckedAt||row.emailCheckedAt||""
            ),
            emailAttemptAt:String(
              update.emailAttemptAt||row.emailAttemptAt||""
            )
          };
        }));

        const checked=Number(result.checked)||0;
        const found=Number(result.found)||0;

        const summary=`${checked} site(s) consultado(s) · ${found} e-mail(s) publico(s) encontrado(s). Entrega nao verificada.`;

        setCollectorProgress(summary);
        setNotice(summary);
        return;
      }

      const rows=dedupe(result.leads||[]);
      // Nova pesquisa substitui o lote anterior; Completar contatos preserva a pesquisa atual.
      if(msg.action==="SEARCH"){
        setImported(rows);
        setExtern({items:[],nextCursor:""});
        setTerm("");
        setEmailOnly(false);
        setPhoneOnly(false);
      }else{
        setImported(old=>dedupe([...old,...rows]));
      }
      setMode("new");setTab("map");setError("");
      const lote=result.details||{};
      const andamento=msg.action==='SYNC'?`Ficha sincronizada: ${rows[0]?.name||'empresa'}. Contato nao verificado.`:`${rows.length} empresas · ${rows.filter(row=>phoneBR(row.phone)).length} com telefone · ${Number(lote.remaining)||0} fichas por examinar`;
      setCollectorProgress(andamento);

      // V4.12 - Continua o levantamento automaticamente.
      const auto=collectorAutoRef.current;

      if(
        auto.active &&
        ["SEARCH","ENRICH"].includes(msg.action)
      ){
        const details=Number(result.remainingDetails);
        const emails=Number(result.remainingEmails);

        if(!Number.isFinite(details) ||
           !Number.isFinite(emails)){
          auto.active=false;
          setError("Collector sem progresso valido. Atualize para v0.7.0.");
          return;
        }

        const pending=details+emails;

        if(pending===0){
          auto.active=false;
          setCollectorProgress("Levantamento concluido.");
          setNotice(
            `Levantamento concluido: ${rows.length} empresas. Confira e exporte seus contatos.`
          );
          return;
        }

        if(details===0 && result.emailPermissionRequired){
          auto.active=false;
          setNotice(
            "Fichas processadas. Autorize a leitura de sites no Collector e utilize Retomar levantamento."
          );
          return;
        }

        auto.stalls=auto.previousPending===pending
          ? auto.stalls+1 : 0;
        auto.previousPending=pending;

        if(auto.steps>=30 || auto.stalls>=2){
          auto.active=false;
          setNotice(
            "Levantamento pausado para evitar repeticoes. Os dados coletados foram preservados."
          );
          return;
        }

        setCollectorProgress(
          `Processando automaticamente: ${details} fichas e ${emails} sites pendentes.`
        );

        setCollectorBusy(true);

        setTimeout(()=>{
          if(!auto.active)return;
          if(collectorRequest.current)return;

          auto.steps++;

          const id=`korax-auto-${Date.now()}-${auto.steps}`;
          collectorRequest.current=id;

          window.postMessage({
            source:"KORAX_PROSPECTION_PAGE_V4",
            type:"ENRICH",
            requestId:id,
            details:4
          },window.location.origin);

          setTimeout(()=>{
            if(collectorRequest.current===id){
              collectorRequest.current="";
              auto.active=false;
              setCollectorBusy(false);
              setError(
                "Tempo limite atingido. O levantamento foi pausado; utilize Retomar levantamento."
              );
            }
          },300000);
        },800);

        return;
      }


      setNotice(msg.action==='SYNC'?`Ficha atualizada com os dados visiveis no Maps. Telefone nao confirma WhatsApp.`:`${andamento}. Complete mais quatro contatos por vez; exporte o JSON para preservar o historico. Enderecos nao verificados.`);
    };
    window.addEventListener("message",handle);
    ping();
    const timer=setInterval(ping,10000);
    return ()=>{clearInterval(timer);window.removeEventListener("message",handle);};
  },[]);
  const collectorAction=(type,lead=null,automatic=false)=>{
    if(!collectorConnected){setError("Extensão não detectada. Baixe, extraia e instale a Korax Collector. Depois recarregue esta página.");return;}
    if(collectorBusy)return;

    if(automatic && !/^0\.7\./.test(collectorVersion)){
      setError(
        "Instale o Collector 0.7.0 para realizar o levantamento completo."
      );
      return;
    }

    if(type==="EMAIL" && !/^0\.6\./.test(collectorVersion)){
      setError("Atualize o Korax Collector para a versao 0.6.0.");
      return;
    }

    const emailCandidates=type==="EMAIL"
      ? imported.filter(item=>{
          if(!safeUrl(item.website)||hasEmail(item.email))
            return false;

          if(item.emailCheckedAt)return false;

          const last=Date.parse(item.emailAttemptAt||"");
          return !Number.isFinite(last)
            || Date.now()-last>=15*60*1000;
        }).slice(0,4)
      : [];

    if(type==="EMAIL" && !emailCandidates.length){
      setNotice("Nenhuma empresa com site e sem e-mail pendente. Para pesquisar novamente, sera necessario iniciar uma nova coleta ou revisar as consultas anteriores.");
      return;
    }
    if(type==="SEARCH" && imported.length && !window.confirm("Iniciar nova pesquisa? Os resultados da pesquisa anterior serao substituidos nesta sessao. Use Exportar com historico antes de continuar para guarda-los.")) return;
    collectorAutoRef.current={
      active:automatic,
      steps:0,
      previousPending:null,
      stalls:0
    };
    setError("");setNotice("");setCollectorBusy(true);
    setCollectorProgress(type==="EMAIL"?"Preparando busca de e-mails públicos…":type==="SEARCH"?"Solicitando pesquisa ao navegador…":type==="SYNC"?"Lendo ficha ja aberta no Google Maps…":"Completando telefones e sites disponíveis…");
    const requestId=`korax-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
    collectorRequest.current=requestId;
    window.postMessage({
      source:"KORAX_PROSPECTION_PAGE_V4",type,requestId,
      query:filters.segment,city:filters.city,uf:filters.uf,
      limit:40,details:4,
      emailLeads:type==="EMAIL"?emailCandidates.map(item=>({
        key:String(mapsKey(item)).slice(0,256),
        name:String(item.name||""),
        website:safeUrl(item.website),
        mapsUrl:String(item.mapsUrl||"")
      })):[],
      name:type==="SYNC"?lead?.name||"":"",mapsUrl:type==="SYNC"?lead?.mapsUrl||"":""
    },window.location.origin);
    setTimeout(()=>{
      if(collectorRequest.current===requestId){
        collectorRequest.current="";setCollectorBusy(false);
        setError("A extensão não respondeu no tempo esperado. Confira as abas do Chrome e tente uma pesquisa menor.");
      }
    },300000);
  };
  // Executa ate quatro paginas de 50 cadastros, respeitando o limite atual do backend (5/min).
  const enrichReceita=async()=>{
    if(receitaBusy || collectorBusy || busy)return;
    const mapped=dedupe(imported).filter(x=>!!x.mapsUrl);
    const code=digits(receitaCnae||inferCnae(filters.segment)||filters.cnae);
    if(!mapped.length){setError("Pesquise empresas com a extensao antes de cruzar com a Receita.");return;}
    if(code.length!==7){setError("Escolha um CNAE de sete digitos para consultar a Receita.");return;}
    if(!filters.city.trim()){setError("Informe a cidade para cruzar o cadastro.");return;}
    setReceitaBusy(true);setError("");setNotice("");setReceitaProgress("Consultando cadastros CNPJ por cidade e CNAE…");
    const candidates=new Map();
    let cursor="",pages=0,interrupted=false;
    try{
      for(let i=0;i<4;i++){
        setReceitaProgress(`Consultando Minha Receita: pagina ${i+1}/4 · ${candidates.size} cadastros analisados.`);
        const params={uf:filters.uf,city:filters.city.trim(),cnae:code,limit:50};
        if(cursor)params.cursor=cursor;
        let data;
        try{({data}=await api.get("/korax-prospection/external",{params,timeout:30000}));}
        catch(err){if(i===0)throw err;interrupted=true;break;}
        pages++;
        (data.items||[]).forEach(row=>{if(digits(row.cnpj).length===14)candidates.set(digits(row.cnpj),row);});
        if(!data.nextCursor || data.nextCursor===cursor)break;
        cursor=data.nextCursor;
      }
      const matches=matchReceiptCandidates(mapped,[...candidates.values()]);
      const patches=new Map();let newEmails=0,newPhones=0;
      for(const {lead,candidate,reason} of matches){
        const patch={};
        if(digits(candidate.cnpj).length===14)patch.cnpj=digits(candidate.cnpj);
        if(!hasEmail(lead.email)&&hasEmail(candidate.email)){patch.email=String(candidate.email).trim();newEmails++;}
        if(!phoneBR(lead.phone)&&phoneBR(candidate.phone)){patch.phone=phoneBR(candidate.phone);newPhones++;}
        if(!lead.decisionMaker&&candidate.probableDecisionMaker)patch.decisionMaker=String(candidate.probableDecisionMaker);
        patch.source=`${lead.source||"Mapa"} · Minha Receita (${reason}; dados nao verificados)`;
        patches.set(mapsKey(lead),patch);
      }
      if(patches.size){
        setImported(old=>old.map(row=>{
          const patch=patches.get(mapsKey(row));
          return patch?{...row,...patch}:row;
        }));
      }
      setReceitaProgress(`${pages} pagina(s), ${candidates.size} cadastros analisados, ${matches.length} correspondencia(s) unica(s).`);
      setNotice(`Minha Receita: ${candidates.size} cadastros consultados em ${pages} pagina(s); ${matches.length} empresa(s) com correspondencia conservadora; ${newEmails} e-mail(s) cadastral(is) e ${newPhones} telefone(s) adicionados.${interrupted?" A consulta foi interrompida pela fonte ou limite: resultados parciais.":""} Os dados nao estao verificados.`);
    }catch(err){setError(`Consulta Minha Receita: ${errorMessage(err)}. Nenhuma correspondencia foi aplicada.`);setReceitaProgress("");}
    finally{setReceitaBusy(false);}
  };
  const importFile=async event=>{
    const file=event.target.files?.[0];if(!file)return;
    try{
      if(file.size>4*1024*1024)throw new Error("Arquivo acima de 4 MB.");
      const text=await file.text();
      let raw;
      if(/\.json$/i.test(file.name)){const payload=JSON.parse(text);raw=Array.isArray(payload)?payload:(payload.leads||payload.items||payload.data);}
      else if(/\.csv$/i.test(file.name))raw=parseCSV(text);
      else throw new Error("Use um arquivo JSON ou CSV.");
      if(!Array.isArray(raw)||raw.length>5000)throw new Error("A lista precisa conter até 5.000 empresas.");
      const items=dedupe(raw);
      setImported(old=>dedupe([...old,...items]));
      setPipeline(old=>{
        const next={...old};items.forEach(x=>{if(!next[x.id])next[x.id]={status:x.status,note:x.note,lastContactAt:x.lastContactAt};});return next;
      });
      setMode("new");setTab("map");setError("");setNotice(`${items.length} negócios importados. A lista está apenas nesta sessão: exporte o histórico para guardar seu trabalho.`);
    }catch(e){setError(e.message||"Falha ao importar arquivo.");}
    finally{event.target.value="";}
  };
  const rawLeads=useMemo(()=>mode==="base"?dedupe(base.items||[]):dedupe([...imported,...extern.items]),[mode,base.items,imported,extern.items]);
  const statusFor=useCallback(item=>pipeline[item.id]?.status||item.status||"new",[pipeline]);
  const center=useMemo(()=>{
    const coords=rawLeads.filter(i=>i.lat!=null&&i.lng!=null);
    if(!coords.length)return null;
    const lats=coords.map(i=>i.lat).sort((a,b)=>a-b),lngs=coords.map(i=>i.lng).sort((a,b)=>a-b);
    return {lat:lats[Math.floor(lats.length/2)],lng:lngs[Math.floor(lngs.length/2)]};
  },[rawLeads]);
  const visible=useMemo(()=>rawLeads.filter(item=>{
    const q=norm(term);const match=!q||norm([item.name,item.address,item.city,item.category,item.phone,item.email,item.decisionMaker,item.cnpj].join(" ")).includes(q);
    return match&&(stage==="all"||statusFor(item)===stage)&&(!emailOnly||hasEmail(item.email))&&(!phoneOnly||!!phoneBR(item.phone))&&(!noSite||!safeUrl(item.website))&&(!highOnly||opportunity(item).score>=70)&&(radius==="all"||!center||item.lat==null||item.lng==null||haversine(center,item)<=Number(radius));
  }).sort((a,b)=>opportunity(b).score-opportunity(a).score),[rawLeads,term,stage,emailOnly,phoneOnly,noSite,highOnly,radius,center,statusFor]);
  const stats=useMemo(()=>({
    all:rawLeads.length,
    phone:rawLeads.filter(i=>phoneBR(i.phone)).length,
    email:rawLeads.filter(i=>hasEmail(i.email)).length,
    site:rawLeads.filter(i=>safeUrl(i.website)).length,
    reachable:rawLeads.filter(i=>phoneBR(i.phone)||hasEmail(i.email)).length,
    high:rawLeads.filter(i=>opportunity(i).score>=70).length,
    opp:rawLeads.filter(i=>statusFor(i)==="opportunity").length
  }),[rawLeads,statusFor]);
  const appliedFilters=[stage!=="all",emailOnly,phoneOnly,noSite,highOnly,radius!=="all"].filter(Boolean).length;
  const updateStatus=(item,status)=>setPipeline(old=>({...old,[item.id]:{...old[item.id],status,lastContactAt:status==="new"?old[item.id]?.lastContactAt||"":new Date().toISOString()}}));
  const toggleCard=id=>setExpandedCards(old=>({...old,[id]:!old[id]}));
  const openNotes=item=>setDialog({channel:"notes",lead:item,body:pipeline[item.id]?.note||item.note||""});
  const snapshot=useCallback(items=>items.map(item=>({...item,status:statusFor(item),note:pipeline[item.id]?.note||item.note,lastContactAt:pipeline[item.id]?.lastContactAt||item.lastContactAt})),[pipeline,statusFor]);
  const copy=async value=>{try{await navigator.clipboard.writeText(value);setNotice("Conteúdo copiado para a área de transferência.");}catch{window.prompt("Copie o texto:",value);}};
  const showDraft=(item,channel)=>{if(statusFor(item)==="blocked")return;setDialog(draftFor(item,offer,channel));};
  const jumpTo=item=>{setSelected(item.id);const el=cardRefs.current[item.id];if(el)el.scrollIntoView({behavior:"smooth",block:"center"});};
  const canContact=lead=>statusFor(lead)!=="blocked";
  const openCnpj=lead=>{
    setCnpjView({lead,record:null});
    setCnpjInput(digits(lead.cnpj || ""));
    setCnpjError("");
  };
  const lookupRegistry=async()=>{
    const cnpj=digits(cnpjInput);
    if(cnpj.length!==14){setCnpjError("Informe um CNPJ de 14 digitos para consultar o cadastro.");return;}
    setCnpjBusy(true);setCnpjError("");
    try{
      const {data}=await api.get(`/korax-prospection/cnpjws/${cnpj}`,{timeout:18000});
      setCnpjView(old=>({...old,record:data}));
    }catch(err){setCnpjError(err?.response?.data?.message || errorMessage(err));}
    finally{setCnpjBusy(false);}
  };
  const registryMatches=()=>{
    const record=cnpjView?.record, lead=cnpjView?.lead;
    if(!record||!lead)return false;
    if(norm(record.uf)!==norm(filters.uf)||norm(record.city)!==norm(filters.city))return false;
    const needle=normalizeCompanyName(lead.name);
    const fantasies=[record.name,record.legalName].map(normalizeCompanyName).filter(Boolean);
    const exact=fantasies.some(n=>n===needle && nameTokens(n).length>=2);
    const bairro=norm(record.neighborhood),addr=norm(lead.address);
    const strong=fantasies.some(n=>nameTokens(needle).length>=3&&similarity(n,needle)>=0.92);
    return exact||(strong&&bairro.length>=5&&addr.includes(bairro));
  };
  const attachRegistry=()=>{
    if(!registryMatches()||mode!=="new"||!cnpjView?.lead?.mapsUrl){setError("Correspondencia insuficiente: nenhum contato foi associado.");return;}
    const {lead,record}=cnpjView;
    const key=mapsKey(lead);
    setImported(old=>old.map(item=>{
      if(mapsKey(item)!==key)return item;
      return {...item,cnpj:record.cnpj,
        cnpjSource:"CNPJ.ws API publica (identidade conferida)",
        email:!hasEmail(item.email)&&hasEmail(record.email)?record.email:item.email,
        phone:!phoneBR(item.phone)&&phoneBR(record.phone)?record.phone:item.phone,
        emailSource:!hasEmail(item.email)&&hasEmail(record.email)?"E-mail cadastral CNPJ.ws (entrega nao verificada)":item.emailSource,
        phoneSource:!phoneBR(item.phone)&&phoneBR(record.phone)?"Telefone cadastral CNPJ.ws":item.phoneSource,
        cnpjLookupAt:new Date().toISOString()
      };
    }));
    if(!imported.some(x=>mapsKey(x)===key)){setError("Empresa nao esta na coleta atual. Importe a pesquisa primeiro.");return;}
    setCnpjView(null);
    setNotice(`Cadastro da ${lead.name} vinculado apos correspondencia forte. E-mail e telefone permanecem nao verificados. Exporte o JSON.`);
  };
  return <Box style={{maxWidth:1580,margin:"0 auto",padding:"18px 20px 44px",color:NAVY,background:"#FAFCFF"}}>
    {/* Cabeçalho em uma linha, com navegação segmentada e estado do coletor. */}
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{gap:14,marginBottom:15}}>
      <Box style={{minWidth:230}}>
        <Typography style={{fontSize:10,color:BLUE,fontWeight:850,letterSpacing:".10em",textTransform:"uppercase",marginBottom:3}}>KORAX / PROSPECÇÃO INTELIGENTE</Typography>
        <Typography style={{fontSize:25,lineHeight:1.18,fontWeight:850,color:NAVY,letterSpacing:"-.65px"}}>Central de Prospecção</Typography>
        <Typography style={{fontSize:12,color:"#71819B",marginTop:4}}>Encontre empresas, complete contatos e organize oportunidades.</Typography>
      </Box>
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:8}}>
        <Box title={collectorConnected?"Coletor instalado e conectado ao navegador":"Instale a extensão para pesquisar no Google Maps"} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 10px",borderRadius:9,background:collectorConnected?"#EDFAF2":"#FFF6E8",color:collectorConnected?"#097344":"#98591B",fontSize:11,fontWeight:750}}>
          <span style={{height:7,width:7,borderRadius:"50%",background:collectorConnected?"#0FAA65":"#E2A144"}} />
          {collectorConnected?"Coletor conectado":"Coletor desconectado"}
        </Box>
        <Box style={{display:"flex",border:`1px solid ${BORDER}`,borderRadius:10,background:"white",padding:3,gap:2}}>
          {[{id:"new",label:"Novos leads"},{id:"base",label:"Minha base"}].map(m=><Button key={m.id} size="small" onClick={()=>{setMode(m.id);setError("");setTerm("");setSelected("");}} style={{background:mode===m.id?NAVY:"transparent",color:mode===m.id?"white":"#596B86",borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:800,textTransform:"none",minWidth:0,boxShadow:"none"}}>{m.label}</Button>)}
        </Box>
      </Box>
    </Box>

    {error&&<Box role="alert" style={{background:"#FFF2F1",border:"1px solid #F6CECD",borderRadius:10,padding:"10px 13px",marginBottom:10,color:"#A11F25",fontSize:12}}>{error}</Box>}
    {notice&&<Box role="status" style={{background:"#EFF6FF",border:"1px solid #DCE9FF",borderRadius:10,padding:"9px 13px",marginBottom:10,color:"#23568F",fontSize:12}}>{notice}</Box>}

    <Card style={{...CARD,marginBottom:12,boxShadow:"0 3px 16px rgba(22,37,78,.035)"}}><CardContent style={{padding:"15px 17px 16px"}}>
      {mode==="new"?<>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{gap:10,marginBottom:14}}>
          <Box><Typography style={{fontSize:15,fontWeight:850,color:NAVY}}>Nova pesquisa</Typography><Typography style={{fontSize:11,color:"#8794A9",marginTop:1}}>Escolha onde buscar empresas</Typography></Box>
          <Box style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:5}}>
            {[{id:"map",label:"Google Maps"},{id:"cnpj",label:"Consultar CNPJ"},{id:"import",label:"Importar lista"}].map(t=><Button key={t.id} size="small" onClick={()=>setTab(t.id)} style={{fontSize:11,fontWeight:750,textTransform:"none",minWidth:0,borderRadius:8,background:tab===t.id?"#EBF2FF":"transparent",color:tab===t.id?BLUE:"#65758D",padding:"6px 10px"}}>{t.label}</Button>)}
          </Box>
        </Box>
        {tab==="map"&&<>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}><TextField fullWidth size="small" variant="outlined" label="Segmento ou atividade" value={filters.segment} onChange={e=>setField("segment",e.target.value)}/></Grid>
            <Grid item xs={8} sm={7} md={3}><TextField fullWidth size="small" variant="outlined" label="Cidade / região" value={filters.city} onChange={e=>setField("city",e.target.value)}/></Grid>
            <Grid item xs={4} sm={5} md={2}><TextField fullWidth select size="small" variant="outlined" label="UF" value={filters.uf} onChange={e=>setField("uf",e.target.value)}>{UF.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3}><Button fullWidth variant="contained" disabled={!collectorConnected||collectorBusy} onClick={()=>collectorAction("SEARCH",null,true)} style={{textTransform:"none",borderRadius:9,minHeight:40,fontWeight:800,background:!collectorConnected||collectorBusy?"#A0AFC5":BLUE,color:"white",boxShadow:"none"}}>{collectorBusy?"Processando levantamento…":"Pesquisar e completar contatos"}</Button></Grid>
          </Grid>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{gap:7,marginTop:12}}>
            <Typography style={{fontSize:11,color:"#71819B"}}>{collectorProgress||(!collectorConnected?"Conecte o coletor para habilitar a pesquisa no Maps.":"Busca via extensão instalada no navegador")}</Typography>
            <Button size="small" onClick={()=>setShowTools(v=>!v)} aria-expanded={showTools} style={{textTransform:"none",fontSize:11,fontWeight:750,color:BLUE,minWidth:0,padding:"3px 7px"}}>Ferramentas e extensão {showTools?"▴":"▾"}</Button>
          </Box>
          {showTools&&<Box style={{background:"#F6F9FE",border:"1px solid #E9EFF9",padding:"10px 11px",borderRadius:10,marginTop:8}}>
            <Box display="flex" flexWrap="wrap" style={{gap:6,alignItems:"center"}}>
              <Typography style={{fontSize:11,fontWeight:750,color:"#576C87",marginRight:5}}>{collectorConnected?`Conectada${collectorVersion?` · v${collectorVersion}`:""}`:"Extensão não detectada"}</Typography>
              <Button size="small" variant="outlined" href="/downloads/korax-collector-v412-auto.zip" download="korax-collector-v412-auto.zip" style={{fontSize:11,textTransform:"none",borderRadius:8}}>Baixar extensão</Button>
              <Button size="small" href="/downloads/korax-collector-instrucoes.txt" target="_blank" rel="noopener noreferrer" style={{fontSize:11,textTransform:"none"}}>Como instalar</Button>
              <Button size="small" href={searchMaps(filters.segment,filters.city,filters.uf)} target="_blank" rel="noopener noreferrer" style={{fontSize:11,textTransform:"none"}}>Abrir Google Maps</Button>
            </Box>
          </Box>}
        </>}
        {tab==="cnpj"&&<Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}><TextField select fullWidth size="small" variant="outlined" label="Segmento / CNAE" value={CNAES.some(c=>c[0]===filters.cnae)?filters.cnae:""} onChange={e=>setField("cnae",e.target.value)}><MenuItem value="">CNAE manual</MenuItem>{CNAES.map(([n,label])=><MenuItem key={n} value={n}>{label} · {n}</MenuItem>)}</TextField></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth size="small" variant="outlined" label="CNAE (7 dígitos)" value={filters.cnae} onChange={e=>setField("cnae",e.target.value)}/></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth size="small" variant="outlined" label="Cidade" value={filters.city} onChange={e=>setField("city",e.target.value)}/></Grid>
          <Grid item xs={5} md={1}><TextField fullWidth select size="small" variant="outlined" label="UF" value={filters.uf} onChange={e=>setField("uf",e.target.value)}>{UF.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
          <Grid item xs={3} md={1}><TextField fullWidth select size="small" variant="outlined" label="Limite" value={filters.limit} onChange={e=>setField("limit",Number(e.target.value))}>{[10,25,50].map(n=><MenuItem key={n} value={n}>{n}</MenuItem>)}</TextField></Grid>
          <Grid item xs={4} md={3}><Button fullWidth variant="contained" disabled={busy} onClick={()=>loadCnpj(false)} style={{textTransform:"none",minHeight:40,fontWeight:750,background:BLUE,color:"white",boxShadow:"none"}}>Buscar CNPJs</Button></Grid>
        </Grid>}
        {tab==="import"&&<Box style={{background:"#F8FAFE",borderRadius:10,padding:13}}>
          <Typography style={{fontSize:12,color:"#667792",marginBottom:9}}>Importe JSON/CSV do Radar ou de uma lista autorizada (até 4 MB e 5.000 empresas). Os dados são lidos somente nesta página.</Typography>
          <FormControlLabel control={<Checkbox color="primary" checked={allowImport} onChange={e=>setAllowImport(e.target.checked)}/>} label={<Typography style={{fontSize:12}}>Tenho direito de utilizar comercialmente esta lista e respeitarei pedidos de não contato.</Typography>}/>
          <Box><Button size="small" variant="contained" disabled={!allowImport} onClick={()=>fileInput.current&&fileInput.current.click()} style={{textTransform:"none",fontWeight:750,background:BLUE,color:"white",boxShadow:"none"}}>Selecionar JSON / CSV</Button></Box>
        </Box>}
        <input ref={fileInput} type="file" accept=".csv,.json" onChange={importFile} style={{display:"none"}}/>
      </>:<>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{marginBottom:12,gap:8}}><Typography style={{fontSize:15,fontWeight:850}}>Minha carteira</Typography><Typography style={{fontSize:11,color:"#8593A7"}}>Contatos existentes na Korax</Typography></Box>
        <Grid container spacing={2} alignItems="center"><Grid item xs={12} md={9}><TextField fullWidth size="small" variant="outlined" label="Buscar contatos da minha base" value={baseSearch} onChange={e=>setBaseSearch(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")loadBase(1);}}/></Grid><Grid item xs={12} md={3}><Button fullWidth variant="contained" disabled={busy} onClick={()=>loadBase(1)} style={{background:BLUE,color:"white",textTransform:"none",minHeight:40,boxShadow:"none"}}>Buscar carteira</Button></Grid></Grid>
      </>}
    </CardContent></Card>

    {mode==="new"&&<Card style={{...CARD,marginBottom:12,background:"#F9FBFF"}}><CardContent style={{padding:"12px 17px"}}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{gap:10}}>
        <Box style={{minWidth:150,flex:"1 1 175px"}}>
          <Typography style={{fontSize:13,fontWeight:800,color:NAVY}}>Enriquecimento</Typography>
          <Typography style={{fontSize:11,color:"#8290A5",marginTop:2}}>Adicione dados às empresas coletadas</Typography>
        </Box>
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:7}}>
          {showSourceInfo&&<TextField size="small" variant="outlined" aria-label="CNAE para enriquecimento" label="CNAE" value={receitaCnae||inferCnae(filters.segment)||filters.cnae} onChange={e=>setReceitaCnae(digits(e.target.value).slice(0,7))} style={{width:112,background:"white"}} InputProps={{style:{height:37,fontSize:12}}}/>}
          
            
          <Button
            size="small"
            variant="outlined"
            disabled={
              !collectorConnected||
              collectorBusy||
              !imported.length||
              !/^0\.7\./.test(collectorVersion)
            }
            onClick={()=>collectorAction("ENRICH",null,true)}
            style={{
              textTransform:"none",
              borderRadius:8,
              fontSize:11,
              fontWeight:750,
              borderColor:"#C4D7F8",
              color:BLUE
            }}
          >Retomar levantamento</Button>
          {showSourceInfo&&<Button size="small" variant="contained" disabled={receitaBusy||collectorBusy||busy||!imported.length} onClick={enrichReceita} style={{fontSize:11,textTransform:"none",fontWeight:800,minHeight:36,borderRadius:8,color:"white",background:BLUE,boxShadow:"none"}}>{receitaBusy?"Consultando Receita…":"Enriquecer com Receita"}</Button>}
          <Button size="small" onClick={()=>setShowSourceInfo(v=>!v)} aria-expanded={showSourceInfo} style={{fontSize:11,textTransform:"none",minWidth:0,color:"#637791",padding:"4px 6px"}}>Opções avançadas {showSourceInfo?"▴":"▾"}</Button>
        </Box>
      </Box>
      {!!receitaProgress&&<Typography role="status" style={{fontSize:11,color:"#436B9E",marginTop:8}}>{receitaProgress}</Typography>}
      {showSourceInfo&&<Box style={{marginTop:10,padding:"10px 11px",background:"white",border:`1px solid ${BORDER}`,borderRadius:9}}>
        <Typography style={{fontSize:11,color:"#64748B",lineHeight:1.55}}>A Minha Receita consulta uma amostra de até 200 cadastros por cidade e CNAE e associa somente correspondências conservadoras. E-mails e telefones não são verificados. A extensão coleta dados do Maps por ação do usuário; a utilização desses dados deve respeitar os termos das fontes. Não contorne bloqueios ou CAPTCHA.</Typography>
        <Typography style={{fontSize:11,color:"#64748B",marginTop:5}}>O enriquecimento não envia mensagens automaticamente. Instalar e atualizar a extensão exige ação manual nesta versão beta.</Typography>
      </Box>}
    </CardContent></Card>}

    <Box display="flex" flexWrap="wrap" style={{gap:9,marginBottom:12}}>
      <Metric value={stats.all} label="Empresas"/>
      <Metric value={stats.phone} label="Com telefone"/>
      <Metric value={stats.email} label="Com e-mail" color="#116D52"/>
      <Metric value={stats.site} label="Com site"/>
      <Metric value={stats.reachable} label="Com canal de contato" color="#116D52"/>
    </Box>

    <Card style={{...CARD,marginBottom:13}}><CardContent style={{padding:"11px 15px"}}>
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:8}}>
        <Box style={{flex:"3 1 250px",minWidth:190}}><TextField fullWidth size="small" variant="outlined" placeholder="Buscar por empresa, endereço ou contato..." value={term} onChange={e=>setTerm(e.target.value)} InputProps={{style:{height:37,fontSize:12}}}/></Box>
        <Box style={{flex:"1 1 155px",minWidth:140}}><TextField fullWidth select variant="outlined" size="small" label="Etapa" value={stage} onChange={e=>setStage(e.target.value)} InputProps={{style:{height:37,fontSize:12}}}><MenuItem value="all">Todos os status</MenuItem>{STATUSES.map(s=><MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}</TextField></Box>
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:3}}>
          <FormControlLabel style={{marginRight:3}} control={<Checkbox color="primary" size="small" checked={phoneOnly} onChange={e=>setPhoneOnly(e.target.checked)}/>} label={<Typography style={{fontSize:11,color:"#50627B"}}>Telefone</Typography>}/>
          <FormControlLabel style={{marginRight:3}} control={<Checkbox color="primary" size="small" checked={emailOnly} onChange={e=>setEmailOnly(e.target.checked)}/>} label={<Typography style={{fontSize:11,color:"#50627B"}}>E-mail</Typography>}/>
        </Box>
        <Button size="small" onClick={()=>setShowMoreFilters(v=>!v)} aria-expanded={showMoreFilters} style={{fontSize:11,textTransform:"none",fontWeight:750,minWidth:0,color:appliedFilters?BLUE:"#556B86",background:appliedFilters?"#EDF4FF":"#F4F7FB",borderRadius:8,padding:"7px 10px"}}>Filtros{appliedFilters?` (${appliedFilters})`:""} {showMoreFilters?"▴":"▾"}</Button>
        <Button size="small" variant="outlined" disabled={!visible.length} onClick={()=>exportJSON(snapshot(visible))} style={{marginLeft:"auto",textTransform:"none",fontSize:11,fontWeight:700,borderRadius:8,borderColor:"#CBD8ED",color:"#536985",minHeight:34}}>Exportar</Button>
      </Box>
      {showMoreFilters&&<Box display="flex" alignItems="center" flexWrap="wrap" style={{gap:10,borderTop:"1px solid #EBF0F7",paddingTop:9,marginTop:10}}>
        <FormControlLabel control={<Checkbox color="primary" size="small" checked={noSite} onChange={e=>setNoSite(e.target.checked)}/>} label={<Typography style={{fontSize:12}}>Sem site</Typography>}/>
        <FormControlLabel control={<Checkbox color="primary" size="small" checked={highOnly} onChange={e=>setHighOnly(e.target.checked)}/>} label={<Typography style={{fontSize:12}}>Potencial ≥ 70</Typography>}/>
        {mode==="new"&&<TextField select variant="outlined" size="small" label="Raio (centro estimado)" value={radius} onChange={e=>setRadius(e.target.value)} style={{width:155}}><MenuItem value="all">Toda a coleta</MenuItem>{[2,5,10,20].map(n=><MenuItem key={n} value={String(n)}>{n} km</MenuItem>)}</TextField>}
        <Button size="small" onClick={()=>{setStage("all");setPhoneOnly(false);setEmailOnly(false);setNoSite(false);setHighOnly(false);setRadius("all");setTerm("");}} style={{fontSize:11,textTransform:"none",marginLeft:"auto"}}>Limpar filtros</Button>
      </Box>}
      {(extern.nextCursor&&mode==="new"||mode==="base")&&<Box display="flex" flexWrap="wrap" style={{gap:7,marginTop:9}}>
        {mode==="new"&&!!extern.nextCursor&&<Button size="small" disabled={busy} variant="outlined" onClick={()=>loadCnpj(true)} style={{fontSize:11,textTransform:"none"}}>Carregar mais CNPJs</Button>}
        {mode==="base"&&<><Button size="small" disabled={busy||basePage<=1} onClick={()=>loadBase(basePage-1)}>Página anterior</Button><Button size="small" disabled={busy||basePage*30>=(base.total||0)} onClick={()=>loadBase(basePage+1)}>Próxima página</Button></>}
      </Box>}
    </CardContent></Card>
    {busy&&<Box textAlign="center" padding={1}><CircularProgress size={23}/></Box>}
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Card style={{...CARD,overflow:"hidden"}}><CardContent style={{padding:10}}>
          <MapPanel leads={visible} selected={selected} onSelect={jumpTo}/>
          <Box padding={1}><Typography style={MUTED}>Mapa: OpenStreetMap/Leaflet. A extensão coleta coordenadas quando o Maps as informa. Empresas sem coordenadas permanecem na lista à direita.</Typography></Box>
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} md={5}>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{marginBottom:10}}>
          <Box><Typography style={{fontWeight:850,fontSize:18}}>Empresas encontradas</Typography><Typography style={MUTED}>{visible.length} empresas nos filtros · ordem por potencial</Typography></Box>
        </Box>
        <Box style={{maxHeight:685,overflowY:"auto",paddingRight:6}}>
          {!visible.length&&<Card style={CARD}><CardContent><Typography style={{fontWeight:700}}>Nenhuma empresa no filtro</Typography><Typography style={MUTED}>Use “Pesquisar com extensão”, importe uma lista ou consulte CNPJ. Verifique os filtros de e-mail e telefone.</Typography></CardContent></Card>}
          {visible.map((item,i)=><div key={item.id} ref={el=>{cardRefs.current[item.id]=el;}}>
            <ProspectCard item={item} index={i} active={selected===item.id} expanded={!!expandedCards[item.id]}
              onSelect={setSelected} onToggle={toggleCard} status={statusFor(item)} onStatus={updateStatus}
              onDraft={showDraft} onNotes={openNotes} onCnpj={openCnpj} onSync={item=>collectorAction("SYNC",item)} syncDisabled={!collectorConnected||collectorBusy||!/^0\.(?:4|5)\./.test(collectorVersion)} note={pipeline[item.id]?.note||item.note}
              lastContactAt={pipeline[item.id]?.lastContactAt||item.lastContactAt}/>
          </div>)}
        </Box>
      </Grid>
    </Grid>
    <Box marginTop={2}><Typography style={MUTED}>Sessão temporária: exporte os resultados para preservá-los. Os contatos informados não foram verificados; nenhum WhatsApp ou e-mail é enviado automaticamente.</Typography></Box>
    <Dialog open={!!cnpjView} onClose={()=>!cnpjBusy&&setCnpjView(null)} fullWidth maxWidth="sm">
      <DialogTitle>Consultar cadastro empresarial</DialogTitle>
      <DialogContent>
        <Typography style={{fontSize:12,color:"#64748B",marginBottom:10}}>Empresa selecionada: <b>{cnpjView?.lead?.name}</b>. A consulta gratuita exige CNPJ conhecido; nao busca por nome. Nenhum dado sera associado sem confirmar a identidade.</Typography>
        <Box display="flex" flexWrap="wrap" style={{gap:10,alignItems:"center"}}>
          <TextField variant="outlined" size="small" label="CNPJ (14 digitos)" value={cnpjInput} onChange={e=>{setCnpjInput(digits(e.target.value).slice(0,14));setCnpjView(old=>({...old,record:null}));}} disabled={cnpjBusy} style={{flex:"1 1 180px"}}/>
          <Button color="primary" variant="contained" disabled={cnpjBusy||cnpjInput.length!==14} onClick={lookupRegistry}>{cnpjBusy?"Consultando…":"Consultar gratis"}</Button>
        </Box>
        {cnpjError&&<Typography role="alert" style={{fontSize:12,color:"#B4232D",marginTop:9}}>{cnpjError}</Typography>}
        {cnpjView?.record&&<Box style={{background:"#F7F9FD",borderRadius:10,padding:13,marginTop:15}}>
          <Typography style={{fontWeight:800,fontSize:15,color:NAVY}}>{cnpjView.record.name||cnpjView.record.legalName}</Typography>
          <Typography style={{fontSize:12,color:"#64748B",marginTop:3}}>{cnpjView.record.legalName} · {cnpjView.record.city}/{cnpjView.record.uf}</Typography>
          <Box marginTop={1}><Typography style={{fontSize:12}}>CNPJ: {cnpjView.record.cnpj}</Typography>
          <Typography style={{fontSize:12}}>Endereço cadastral: {cnpjView.record.address||"Nao informado"}</Typography>
          <Typography style={{fontSize:12}}>E-mail cadastral: {cnpjView.record.email||"Nao informado"}</Typography>
          <Typography style={{fontSize:12}}>Telefone cadastral: {cnpjView.record.phone?fmtPhone(cnpjView.record.phone):"Nao informado"}</Typography></Box>
          {!!cnpjView.record.registeredPeople?.length&&<Box marginTop={1}><Typography style={{fontSize:11,fontWeight:800}}>QUADRO SOCIETARIO PUBLICO (NAO CONFIRMA DECISOR)</Typography>
            {cnpjView.record.registeredPeople.map((s,i)=><Typography key={i} style={{fontSize:12}}>{s.name} · {s.role}</Typography>)}
          </Box>}
          {cnpjView.record.omittedByRequest&&<Typography style={{fontSize:11,color:"#A86A25",marginTop:8}}>Alguns dados foram omitidos a pedido do titular; nao serao buscados por outra via nesta consulta.</Typography>}
          <Box marginTop={1}><Typography style={{fontSize:12,color:registryMatches()?"#08794E":"#AE3939",fontWeight:700}}>{registryMatches()?"Correspondencia forte de nome e municipio: pode confirmar a vinculacao.":"Identidade inconclusiva: nao associar dados automaticamente."}</Typography></Box>
        </Box>}
        <Typography style={{fontSize:11,color:"#64748B",marginTop:11}}>Fonte: CNPJ.ws API publica. Dados cadastrais podem estar desatualizados ou ser do contador. Nao comprova entrega de e-mail, WhatsApp ou quem decide compras. Nao envia mensagens.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={()=>setCnpjView(null)} disabled={cnpjBusy}>Fechar</Button>
        <Button variant="contained" color="primary" disabled={cnpjBusy||!registryMatches()||mode!=="new"||!cnpjView?.lead?.mapsUrl} onClick={attachRegistry}>Confirmar e adicionar dados</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={!!dialog} onClose={()=>setDialog(null)} fullWidth maxWidth="sm">
      {dialog&&<>
        <DialogTitle style={{color:NAVY,fontWeight:800}}>{dialog.channel==="call"?"Roteiro de ligação":dialog.channel==="email"?"Preparar e-mail":dialog.channel==="whatsapp"?"Preparar WhatsApp":"Notas da empresa"} — {dialog.lead.name}</DialogTitle>
        <DialogContent>
          {dialog.channel!=="notes"&&<>
            <Typography style={MUTED}>Revise a abordagem. Nenhuma mensagem é enviada automaticamente.</Typography>
            <Box marginTop={2}><TextField fullWidth variant="outlined" size="small" label="Sua oferta / posicionamento" value={offer} onChange={e=>{const v=e.target.value;setDialog(old=>({...old,body:old.body.replace(offer,v)}));setOffer(v);}}/></Box>
            {dialog.channel==="email"&&<Box marginTop={2}><TextField fullWidth variant="outlined" size="small" label="Assunto" value={dialog.subject} onChange={e=>setDialog(old=>({...old,subject:e.target.value}))}/></Box>}
          </>}
          <Box marginTop={2}><TextField fullWidth multiline rows={dialog.channel==="notes"?5:8} variant="outlined" label={dialog.channel==="notes"?"Observações comerciais":"Texto / roteiro (editável)"} value={dialog.body} onChange={e=>setDialog(old=>({...old,body:e.target.value}))}/></Box>
          {dialog.channel==="call"&&<Box marginTop={2}><Typography variant="body2">Contato: {fmtPhone(dialog.lead.phone)}. Pergunte quem coordena o atendimento e identifique necessidades antes de apresentar uma solução.</Typography></Box>}
          {dialog.channel==="whatsapp"&&<Box marginTop={2}><Typography variant="body2">O número é da empresa, não foi confirmado como WhatsApp ativo. Abrir a conversa não confirma entrega.</Typography></Box>}
        </DialogContent>
        <DialogActions style={{padding:16,flexWrap:"wrap"}}>
          <Button onClick={()=>setDialog(null)}>Fechar</Button>
          {dialog.channel==="notes"?<Button variant="contained" color="primary" onClick={()=>{setPipeline(old=>({...old,[dialog.lead.id]:{...old[dialog.lead.id],note:dialog.body}}));setDialog(null);setNotice("Nota atualizada nesta sessão. Exporte o histórico para preservar.");}}>Salvar nota</Button>:<>
            <Button variant="outlined" onClick={()=>copy(`${dialog.channel==="email"?`Assunto: ${dialog.subject}\n\n`:""}${dialog.body}`)}>Copiar texto</Button>
            {dialog.channel==="whatsapp"&&<Button variant="contained" style={{background:"#11935B",color:"#fff"}} onClick={()=>window.open(`https://wa.me/55${phoneBR(dialog.lead.phone)}?text=${encodeURIComponent(dialog.body)}`,"_blank","noopener,noreferrer")}>Testar WhatsApp (nao verificado)</Button>}
            {dialog.channel==="call"&&<Button variant="contained" color="primary" href={`tel:+55${phoneBR(dialog.lead.phone)}`}>Abrir discador</Button>}
            {dialog.channel==="email"&&<>
              <Button variant="outlined" href="/email" target="_blank" rel="noopener noreferrer">E-mail Korax</Button>
              <Button variant="contained" color="primary" href={`mailto:${encodeURIComponent(dialog.lead.email)}?subject=${encodeURIComponent(dialog.subject)}&body=${encodeURIComponent(dialog.body)}`}>Abrir e-mail</Button>
            </>}
          </>}
        </DialogActions>
      </>}
    </Dialog>
  </Box>;
};
export default KoraxProspection;
